'use client';

import React, { useState } from 'react';
import { PlayerRef } from '@remotion/player';
import { useTranslations } from 'next-intl';
import { Chip } from '@crabs-ai/ds-react';
import { Movie, MusicNote, VoiceSelection } from '@crabs-ai/web-icons';
import AiVideoContent from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/ai-video/AiVideoContent';
import AiVoiceGenerateDialog from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/ai-source/AiVoiceGenerateDialog';
import AiVideoGenerateDialog from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/ai-source/AiVideoGenerateDialog';
import AiSourceGeneratedFeed from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/ai-source/AiSourceGeneratedFeed';
import { useProjectTypeContext } from '@/features/remotion-editor/editor/contexts/project-type-context';

type ResultFilter = 'all' | 'video' | 'audio';

interface AiSourceContentProps {
  projectId: number;
  playerRef: React.RefObject<PlayerRef | null>;
}

export default function AiSourceContent({
  projectId,
  playerRef,
}: AiSourceContentProps) {
  const t = useTranslations();
  const { projectType } = useProjectTypeContext();
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all');
  const [isAiVoiceDialogOpen, setIsAiVoiceDialogOpen] = useState(false);
  const [isAiVideoDialogOpen, setIsAiVideoDialogOpen] = useState(false);

  return (
    <div className="pb-layout-bottom flex h-full min-h-0 w-full flex-col overflow-hidden">
      <div className="flex flex-col gap-xs px-md py-sm">
        <button
          type="button"
          className="flex min-h-10 w-full cursor-pointer flex-col items-center justify-center gap-4xs rounded-xs bg-sys-style-neutral-light p-xs transition-colors hover:bg-sys-style-neutral-default"
          onClick={() => setIsAiVoiceDialogOpen(true)}
        >
          <VoiceSelection className="size-6 [&_path]:fill-sys-cont-neutral-default" />
          <span className="text-title-small text-sys-cont-neutral-default">
            {t('Editor.ai-source.action.audio')}
          </span>
        </button>

        <button
          type="button"
          className="flex min-h-10 w-full cursor-pointer flex-col items-center justify-center gap-4xs rounded-xs bg-sys-style-neutral-light p-xs transition-colors hover:bg-sys-style-neutral-default"
          onClick={() => setIsAiVideoDialogOpen(true)}
        >
          <Movie className="size-6 [&_path]:fill-sys-cont-neutral-default" />
          <span className="text-title-small text-sys-cont-neutral-default">
            {t('Editor.ai-source.action.video')}
          </span>
        </button>
      </div>

      <div className="flex items-center gap-4xs px-md py-xs">
        <h3 className="text-title-medium text-sys-cont-neutral-default">
          {t('Editor.ai-source.generated.title')}
        </h3>
      </div>

      <div className="flex items-center gap-2xs px-md py-2xs">
        <Chip
          size="md"
          style="filled"
          selected={resultFilter === 'all'}
          labelNode={t('Editor.ai-source.filter.all')}
          onClick={() => setResultFilter('all')}
        />
        <Chip
          size="md"
          style="filled"
          selected={resultFilter === 'video'}
          isShowLeadingIcon
          leadingIconNode={<Movie className="size-sm [&_path]:fill-current" />}
          labelNode={t('Editor.ai-source.filter.video')}
          onClick={() => setResultFilter('video')}
        />
        <Chip
          size="md"
          style="filled"
          selected={resultFilter === 'audio'}
          isShowLeadingIcon
          leadingIconNode={
            <MusicNote className="size-sm [&_path]:fill-current" />
          }
          labelNode={t('Editor.ai-source.filter.audio')}
          onClick={() => setResultFilter('audio')}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        {projectType ? (
          <div className="h-full overflow-y-auto px-md py-sm layout-padding-bottom">
            <AiSourceGeneratedFeed
              projectId={projectId}
              playerRef={playerRef}
              projectType={projectType}
              filter={resultFilter}
            />
          </div>
        ) : (
          <AiVideoContent
            projectId={projectId}
            playerRef={playerRef}
            variant="generated-only"
          />
        )}
      </div>

      <AiVoiceGenerateDialog
        open={isAiVoiceDialogOpen}
        onClose={() => setIsAiVoiceDialogOpen(false)}
        projectId={projectId}
        playerRef={playerRef}
      />
      <AiVideoGenerateDialog
        open={isAiVideoDialogOpen}
        onClose={() => setIsAiVideoDialogOpen(false)}
        projectId={projectId}
        playerRef={playerRef}
      />
    </div>
  );
}
